/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/
import React, { useMemo, useState, useEffect } from "react";

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
	setTeamFilter,
	setGPSStatDefaults,
	setTeamPresets,
} from "../../store/actions/team.actions.js";
import { useDispatch } from "react-redux";
import { setPlayerFilter } from "../../store/actions/player.actions.js";
import { Switch, Row, Col, Button, Select } from "antd";
import { audioMessage as message } from "../Common/Notifications/audioMessage";
import "../../Styles/styles-ResultsTable.css";
import "../../Styles/styles-Example.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { getDropdownKey } from "../../Utils/chart_utils";
import { trainingAPI } from "../../api/TrainingAPI";
import {
	MAX_VELO_METRICS,
	TOTAL_DISTANCE_METRICS,
	getMetricsToRender
} from "../../Utils/chart_utils.jsx";
import { useSelector } from "react-redux";
import { StatTileV2 } from "../StatTileV2/StatTileV2.jsx";
import BarChartHighcharts from "../Charts/BarChart.jsx";
import { mapDataForHighcharts, mapDataForMaxVeloChart } from "../../mappers/model/BarChartMapper.js";
import { stackedBarChartDtoToModel } from "../../mappers/model/StackedBarChartMapper.js";
import {mapDataForHighchartsGroupedBar} from "../../mappers/model/GroupedBarChartMapper.js";
import { mapDataForBarAndBubbleChart } from "../../mappers/model/BarAndBubble.js";
import { useMetricDropdowns } from "../../hooks/useDropdownInitialization.js"
import { CompactStatTile } from "../StatTileV2/CompactStatTile.jsx";
import { useAuth0 } from "@auth0/auth0-react";
import { PresetSelection, PresetType } from "../PresetSelection.tsx";


export function PlayerCharts({
	dataIn,
	player_data = false,
	showACWR,
	showTravel,
	showRest,
	type,
	dataColorUtil = undefined
}) {
	const dispatch = useDispatch();
	const PRESET_TYPE = PresetType.Gps;
  	const DEFAULT_PRESET = 'Default'

	const {
		games,
		info: { display_name: teamName, metric: teamMetric, restAndTravel, team_colors: teamColors, custom_charts: customChartData,
			acwr_lower_limit: ACWRLowerLimit, acwr_upper_limit: ACWRUpperLimit
		},
		gpsStatDefaults,
		dropdowns: {gps: gpsDropdowns},
		all_metric_types,
    	misc: { comparison_types },
		presets
	} = useSelector(({ team }) => team);
	const {
		filter: {
			player,
			showPredictions,
			startDate,
			endDate,
			gpsData,
		},
		playerCharts
	} = useSelector(({ player }) => player);

	const {
		user,
	  } = useAuth0();
	
	const [statMetrics, setStatMetrics] = useState([]);
	const [option, setOption] = useState("latest_vs_prev");
	const [currentSelectedTilePreset, setCurrentSelectedTilePreset] = useState(DEFAULT_PRESET)
	const [unsavedChanges, setUnsavedChanges] = useState(false)


	let data = dataIn["data"];
	// const defaultMetricObjects = useMemo(() => {
	// 	return gpsStatDefaults.map((metricIdentifier) =>
	// 		all_metric_types["gps"].find(
	// 			(metric) =>
	// 				getDropdownKey(metric, "metricId") === metricIdentifier
	// 		)
	// 	);
	// }, [gpsStatDefaults, all_metric_types]);

	useMetricDropdowns(['gps']);


	const handleChangeMetric = (index) => (key) => {
		setStatMetrics((prevStatMetrics) => {
			const newStatMetrics = [...prevStatMetrics];
			newStatMetrics[index] = key;
			return newStatMetrics;
		});
		setUnsavedChanges(true)
	};

	const onChangeStats = () => {
		if (type === "team") {
			dispatch(setTeamFilter({ gpsData: !gpsData }));
		} else if (type === "player") {
			dispatch(setPlayerFilter({ gpsData: !gpsData }));
		}
	};

	for (let i = 0; i < dataIn.metrics.length; i++) {
		let metric;
		let wellness;
		if (i < dataIn["metrics"].length) {
			metric = dataIn["metrics"][i];
			wellness = false;
		}

		let key = metric.key,
			maxValue = 0;

		data.map((item, index) => {
			let currVal = data[index][key];
			let per_minute_value = 0;
			if (currVal > maxValue) maxValue = currVal;
			if (item[key + "_per_minute"]) {
				per_minute_value = +item[key + "_per_minute"];
			} else {
				per_minute_value = (+item["total_time"] > 0 ? +(+item[key]/ +item["total_time"]).toFixed(2) : 0)
			}
			const itemWithPerMin = {
				...item,
				[key + "_per_minute"]: per_minute_value,
			};

			dataIn.data[index][key + "_per_minute"] = per_minute_value;

			if (wellness === true || index < 2) {
				return { ...itemWithPerMin };
			} else {
				return {
					...itemWithPerMin,
					[key + "_rolling_average"]:
						(currVal +
							data[index - 1][key] +
							data[index - 2][key]) /
						3,
				};
			}
		});
	}

	const handleSaveMetrics = async () => {
		try {
			if (currentSelectedTilePreset === DEFAULT_PRESET) {
				await trainingAPI.updateDefaultStats(statMetrics, "gps");
				dispatch(setGPSStatDefaults(statMetrics));
			} else {
				const presetModified = presets?.find((preset) => preset.name === currentSelectedTilePreset)
				await trainingAPI.updatePreset({
					name: currentSelectedTilePreset, type: PRESET_TYPE, config: statMetrics, preset_id: presetModified.preset_id
				});
				presetModified.config = statMetrics
				dispatch(setTeamPresets(presets))
			}
			message.success("Saved!");
		} catch (e) {
			console.log(e);
			message.error("Error. Unable to edit ");
		}
	};

	const getMappingFunction = (metric) => {

		let customChart = null;
		let chartType = null;
		if(metric?.customChartData) {
			customChart = metric?.customChartData
			chartType = metric?.customChartData?.type
		}

		switch (chartType) {
			case 'bar_and_bubble_chart': {
				return mapDataForBarAndBubbleChart({
					dataIn,
					mainSeriesMetric: dataIn.metrics.find( (metric) => metric.key === customChart.bar_metric),
					secondarySeriesMetric: dataIn.metrics.find( (metric) => metric.key === customChart.bubble_metric),
					showACWR,
					showRest,
					showTravel,
					restAndTravel,
					startDate,
					endDate,
					teamName,
					showPredictions,
					games,
					teamColors: teamColors.all,
					showAvgPlotline: true,
					ACWRLowerLimit,
					ACWRUpperLimit,
					customTitle: customChart?.title,
					dataColorUtil,
					enableDrillDown: true,
					selectedPlayerId: player
				});
			}
			case 'grouped_bar_chart': {
				return mapDataForHighchartsGroupedBar({
					dataIn,
					games,
					metrics: dataIn.metrics.filter( (metric) => metric.key === customChart.bar1_metric || metric.key === customChart.bar2_metric),
					teamColors: teamColors.all,
					isComparison: false,
					showACWR,
					showRest,
					showTravel,
					restAndTravel,
					startDate,
					endDate,
					showPredictions,
					isTeamChart: false,
					isMetricComparisonChart: true,
					title: customChart.title,
					dataColorUtil,
					enableDrillDown: true,
					selectedPlayerId: player
				});
			}
			case 'stacked_bar_chart': {
				return stackedBarChartDtoToModel({
				  data: dataIn?.data,
				  showRest,
				  showTravel,
				  restAndTravel,
				  startDate,
				  endDate,
				  chartType: customChart?.stack_type,
				  showPredictions,
				  colors_100: dataIn?.colors,
				  games,
				  teamName,
				  showACWR: customChart?.stack_type === 'distance' ? false : true,
				  customChartConfig: customChart,
				  metrics: dataIn?.metrics,
				  ACWRLowerLimit,
          		  ACWRUpperLimit,
				  enableDrillDown: true,
				  selectedPlayerId: player
				});
			}
			default: {
				// custom chart type either not provided or not recognized.
				// Will get handled by the code below
			}
		}

		if(MAX_VELO_METRICS.concat(TOTAL_DISTANCE_METRICS).includes(metric.key)) {
		  return mapDataForMaxVeloChart({
			dataIn,
			metric,
			showRest,
			showTravel,
			restAndTravel,
			startDate,
			endDate,
			showACWR,
			games,
			teamName: teamName,
			showPredictions,
			teamColors: teamColors.all,
			ACWRLowerLimit, 
			ACWRUpperLimit,
			dataColorUtil,
			enableDrillDown: true,
			selectedPlayerId: player
		  })
		}
		 
		return mapDataForHighcharts({
			dataIn,
			metric,
			showACWR,
			showRest,
			showTravel,
			restAndTravel,
			startDate,
			endDate,
			games,
			teamName: teamName,
			showPredictions,
			teamColors: teamColors.all,
			teamMetric,
			ACWRLowerLimit,
			ACWRUpperLimit,
			dataColorUtil,
			enableDrillDown: true,
			selectedPlayerId: player
		});
	}

	const metricsToRender = getMetricsToRender(teamName, dataIn?.metrics, customChartData?.chart_types, customChartData?.hidden_bar_charts_metric_keys);

	return (
		<>
			<div
				className="flex flex-col justify-center items-center w-full"
				id="statistics-widget"
			>
				<Row
				gutter={16}
				style={{ width: "80%", marginBottom: 16, justifyContent: 'center', gap: '10px' }}>
				<PresetSelection 
					presets={presets} 
					user={user} 
					presetType={PRESET_TYPE} 
					statDefaults={gpsStatDefaults} 
					currStatMetrics={statMetrics}
					setStatMetrics={setStatMetrics} 
					currentSelectedTilePreset={currentSelectedTilePreset}
					setCurrentSelectedTilePreset={setCurrentSelectedTilePreset}
					comparisonTypes={comparison_types}
					comparisonOption={option}
					setComparisonOption={setOption}
					handleSave={handleSaveMetrics}
					warnUnsavedChanges={unsavedChanges}
					setWarnUnsavedChages={setUnsavedChanges}
					/>
				</Row>
	  		</div>
			<div
				className="flex flex-col justify-center items-center w-full"
				id="statistics-widget"
			>
				<Row
					gutter={16}
					style={{ width: "90%", marginBottom: 16 }}
					className="exportable tab-testing testing-stats"
				>
					{statMetrics?.map((metric, i) => {
						return (
							<Col span={6} key={i}>

							<CompactStatTile
								dropdowns={gpsDropdowns}
								metric_identifier_with_stat={metric}
								onChange={handleChangeMetric(i)}
								option={option}
								endDate={endDate}
								startDate={startDate}
								isCompact={true}
								type={type}
								comparison={option}
								playerId={player}			
								/>

								{/* <StatTileV2
									dropdowns={gpsDropdowns}
									value={getDropdownKey(metric, "metricId")}
									index={i}
									onChange={handleChangeMetric(i)}
									metricType={"gps"}
									endDate={endDate}
									stat={gpsData ? "Max" : "Avg"}
									option={option}
									{...(type === "player" && {
										playerId: player,
									})}
									type={type}
								/> */}
							</Col>
						);
					})}
				</Row>
			</div>
				<div className="outer-example-grid">
					<ul className="list-unstyled example-table-grid" style={{gap: '3rem'}}>
						{
							(dataIn?.metrics?.length && statMetrics?.length && customChartData?.chart_types && metricsToRender?.length) ? (
								metricsToRender
								.map( (metric, i) => 
									<div className="example-table-graph" key={`${metric?.key}_${i}`}>
										<BarChartHighcharts 
											options={{
												...getMappingFunction(metric),
												chart: {
													...getMappingFunction(metric).chart,
													height: '100%'
												}
											}} 
										/>
									</div>)
						): <></>}
					</ul>
				</div>
		</>
	);
}
